import data from '@emoji-mart/data/sets/14/native.json' with { type: 'json' }
import { Node } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { PasteRule } from '@tiptap/react'
import { Suggestion, SuggestionOptions } from '@tiptap/suggestion'
import { init, SearchIndex, Store } from 'emoji-mart'

import { PickerEmoji } from '../../../Picker/index.js'

import { renderEmojiPopup } from './options.js'

export type EmojiOptions = {
  HTMLAttributes: Record<string, unknown>
  suggestion: Omit<SuggestionOptions, 'editor'>
}

export const EmojiPluginKey = new PluginKey('emoji')

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const TRIGGER_CHAR = ':'

const searchEmoji = async (
  query: string,
  { maxResults } = { maxResults: 20 },
): Promise<PickerEmoji[]> => {
  // will noop if already initialized
  init({ data })

  const storedSkinTone = Store.get('skin')
  const skinTone = storedSkinTone !== undefined ? storedSkinTone - 1 : 0
  const searchParams = { maxResults, caller: noop }

  // Some emoji's emoticon representation start with a colon. For example, `:)` or `:D`.
  // Since the query string here is whatever the user types after the colon char, we need to
  // prepend the colon to the query to include correct emojis that start with a colon.
  // So searching with and without the colon will give us the correct results i.e. :smile and :)
  const [withColonEmojis, withoutColonEmojis] = await Promise.all([
    SearchIndex.search(`${TRIGGER_CHAR}${query}`, searchParams),
    SearchIndex.search(query, searchParams),
  ])

  const allEmojis = [...withColonEmojis, ...withoutColonEmojis]

  return allEmojis.map(it => {
    const skin = it.skins?.[skinTone] ?? it.skins[0]
    return {
      id: it.id,
      name: it.name,
      ...skin,
    }
  })
}

const defaultSuggestions: PickerEmoji[] = [
  {
    id: 'heart',
    shortcodes: ':heart:',
    native: '❤️',
    unified: '2764-fe0f',
  },
  {
    id: 'tada',
    shortcodes: ':tada:',
    native: '🎉',
    unified: '1f389',
  },
  {
    id: 'smile',
    shortcodes: ':smile:',
    native: '😄',
    unified: '1f604',
  },
  {
    id: 'open_mouth',
    shortcodes: ':open_mouth:',
    native: '😮',
    unified: '1f62e',
  },
  {
    id: 'cry',
    shortcodes: ':cry:',
    native: '😢',
    unified: '1f622',
  },
]
const EMOJI_INPUT_REGEX = /:([^:\s]*(?:::[^:\s]*)*):/g

export const Emoji = Node.create<EmojiOptions>({
  name: 'emoji',

  addOptions() {
    return {
      HTMLAttributes: {},
      suggestion: {
        char: TRIGGER_CHAR,
        pluginKey: EmojiPluginKey,
        items: async ({ query }) => {
          if (!query || query.length < 1) {
            const thumbUpEmoji = (await searchEmoji('+1'))?.[0]
            return [thumbUpEmoji, ...defaultSuggestions]
          }

          return searchEmoji(query)
        },
        // TODO: @Mehrad we should pass the document from the `useDocument` hook
        render: () => renderEmojiPopup({ document }),
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const { nodeAfter } = editor.view.state.selection.$to
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: 'text',
                text: props.native,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()
        },
      },
    }
  },

  group: 'inline',

  inline: true,

  atom: true,

  addPasteRules() {
    const pasteRule = new PasteRule({
      find: EMOJI_INPUT_REGEX,
      handler: async ({ state, range, match }) => {
        const emojiSuggestions = await searchEmoji(match[1], {
          maxResults: 1,
        })

        if (!emojiSuggestions || emojiSuggestions?.length < 1) {
          return
        }

        const emoji = emojiSuggestions[0]
        const insert = emoji?.native
        const start = range.from
        const end = range.to

        state.tr.insertText(insert, start, end)
      },
    })

    return [pasteRule]
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
